import { useEffect, useState } from "react";
import { Building2, UserRound } from "lucide-react";
import api from "@/lib/api";
import { Reveal } from "@/components/site/Reveal";
import { Eyebrow, SectionTitle } from "@/components/site/Bits";
import { useI18n } from "@/i18n/I18nContext";

function useCountUp(target, active, duration = 1600) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active || target == null) return undefined;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
      else setVal(target);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return val;
}

function FigureCard({ icon: Icon, value, label, testid }) {
  return (
    <div className="relative glass rounded-3xl p-8 sm:p-10 card-hover overflow-hidden" data-testid={testid}>
      <div className="aura w-[220px] h-[220px] bg-cyan-600/20 -top-16 -right-10" />
      <span className="relative inline-flex w-12 h-12 rounded-2xl bg-brand-gradient items-center justify-center shadow-lg shadow-blue-500/25">
        <Icon className="w-6 h-6 text-white" strokeWidth={1.75} />
      </span>
      <p className="relative mt-6 font-display font-black tracking-tighter text-5xl sm:text-6xl text-gradient leading-none tabular-nums">
        +{Math.max(0, value).toLocaleString("fr-FR")}
      </p>
      <p className="relative mt-3 text-base sm:text-lg text-zinc-300 font-medium">{label}</p>
    </div>
  );
}

export function KeyFigures() {
  const { t } = useI18n();
  const [m, setM] = useState(null);

  useEffect(() => {
    api.get("/public/metrics").then(({ data }) => setM(data)).catch(() => setM(false));
  }, []);

  const ready = m && typeof m === "object";
  const companies = useCountUp(ready ? m.companies_supported : 0, ready);
  const pros = useCountUp(ready ? m.professionals_equipped : 0, ready);

  if (m === false || m === null) return null;

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24" data-testid="home-key-figures">
      <Reveal>
        <div className="text-center">
          <Eyebrow>{t("numbers.eyebrow")}</Eyebrow>
          <SectionTitle>{t("numbers.title")}</SectionTitle>
        </div>
      </Reveal>
      <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 max-w-4xl mx-auto">
        <Reveal delay={0.05}>
          <FigureCard icon={Building2} value={companies} label={t("numbers.companies")} testid="kf-companies" />
        </Reveal>
        <Reveal delay={0.15}>
          <FigureCard icon={UserRound} value={pros} label={t("numbers.professionals")} testid="kf-professionals" />
        </Reveal>
      </div>
    </section>
  );
}
